/**
 * Gmail OAuth Callback Handler
 * Handles the redirect from Google OAuth and exchanges the code for tokens
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';
import { exchangeCodeForTokens } from '../utils/gmailAuth';
import { getMailboxConfig, ensureExecGmailWatch } from '../utils/execInboxApi';
import './GmailCallback.css';

const GmailCallback: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    const handleCallback = async () => {
      const code = searchParams.get('code');
      const error = searchParams.get('error');

      if (error) {
        setStatus('error');
        setErrorMessage(`Google authorization failed: ${error}`);
        return;
      }

      if (!code) {
        setStatus('error');
        setErrorMessage('No authorization code received from Google.');
        return;
      }

      if (!user?.id) {
        setStatus('error');
        setErrorMessage('You must be logged in to connect Gmail.');
        return;
      }

      try {
        setStatus('processing');
        const result = await exchangeCodeForTokens(code, user.id);
        
        if (result.success) {
          setStatus('success');
          try {
            const mbCfg = await getMailboxConfig(user.id);
            if (mbCfg.enabled) {
              await ensureExecGmailWatch(user.id);
            }
          } catch {
            /* push watch is best-effort after OAuth */
          }
          // Redirect to the Executive Suite after a short delay
          setTimeout(() => {
            navigate('/executive');
          }, 2000);
        } else {
          setStatus('error');
          setErrorMessage(result.error || 'Failed to connect Gmail account.');
        }
      } catch (err) {
        console.error('OAuth callback error:', err);
        setStatus('error');
        setErrorMessage('An unexpected error occurred while connecting Gmail.');
      }
    };

    handleCallback();
  }, [searchParams, user?.id, navigate]);

  return (
    <div className="gmail-callback-page">
      <div className="callback-card">
        {status === 'processing' && (
          <>
            <div className="callback-spinner"></div>
            <h2>Connecting Gmail...</h2>
            <p>Please wait while we complete the connection.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="callback-icon success">✓</div>
            <h2>Gmail Connected!</h2>
            <p>Redirecting you to the Executive Suite...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="callback-icon error">✕</div>
            <h2>Connection Failed</h2>
            <p>{errorMessage}</p>
            <button 
              className="retry-btn"
              onClick={() => navigate('/executive')}
            >
              Go to Executive Suite
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default GmailCallback;
